import { app, authentication } from "@microsoft/teams-js";
import { toast } from "react-toastify";

/**
 * Service voor het beheren van authenticatie en tokens
 * Handelt Teams SSO en API token exchange af
 */
export class AuthenticationService {
    private static instance: AuthenticationService;
    private static baseUrl: string;
    private static token: string = '';
    private static tokenPromise: Promise<void> | null = null;
    private static tokenExpiry: number | null = null;
    private static refreshInterval: NodeJS.Timeout | null = null;

    private constructor() {}

    public static getInstance(): AuthenticationService {
        if (!AuthenticationService.instance) {
            AuthenticationService.instance = new AuthenticationService();
        }
        return AuthenticationService.instance;
    }

    /**
     * Initialiseer de authenticatie service
     * Moet aangeroepen worden voordat andere methodes gebruikt worden
     * Haalt meteen een token op zodat de app authenticated is
     */
    public static async init(): Promise<void> {
        AuthenticationService.baseUrl = await AuthenticationService.getBaseUrl();
        await AuthenticationService.setToken();
        AuthenticationService.startTokenRefreshInterval();
    }

    /**
     * Haal de bearer token op
     * Zorgt automatisch voor refresh als de token expired is
     * @returns Bearer token voor API authenticatie
     */
    public static async getToken(): Promise<string> {
        if (!AuthenticationService.token || AuthenticationService.isTokenExpiringSoon()) {
            await AuthenticationService.setToken();
        }
        return AuthenticationService.token;
    }

    /**
     * Haal de base URL van de API op
     * @returns Base URL van de API
     */
    public static getBaseUrl(): Promise<string> {
        return AuthenticationService.determineBaseUrl();
    }

    /**
     * Stop de token refresh interval
     * Moet aangeroepen worden bij cleanup (bijv. component unmount)
     */
    public static cleanup(): void {
        if (AuthenticationService.refreshInterval) {
            clearInterval(AuthenticationService.refreshInterval);
            AuthenticationService.refreshInterval = null;
        }
    }

    /**
     * Forceer een token refresh
     */
    public static async forceRefresh(): Promise<void> {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('tokenExpiry');
        AuthenticationService.token = '';
        AuthenticationService.tokenExpiry = null;
        await AuthenticationService.setToken();
    }

    // ========== Private Methods ==========

    private static isLocalEnv(): boolean {
        // Check NODE_ENV
        const isDevelopment = process.env.NODE_ENV === 'development';

        // Check hostname
        const isLocalHost = window.location.hostname === "localhost";

        return isDevelopment || isLocalHost;
    }

    private static async determineBaseUrl(): Promise<string> {
        if (AuthenticationService.isLocalEnv()) {
            return "https://localhost:56800";
        }

        const context = await app.getContext();
        const team = context.sharePointSite?.teamSitePath;

        const projectName = team
            ?.replace("/sites/", "")
            .replace("-02.Opvolgen", "")
            .replace("-Opvolgen", "")
            .replace("-FollowUp", "")
            .replace("-02.FollowUp", "");

        const envName = projectName?.replace(/\s+/g, "").match(/DOSP-([a-z,A-Z,-]*)-\d+/);
        if (!envName) {
            toast.error(
                "Er is iets misgegaan bij het laden van de gegevens. Neem contact op met je systeembeheerder."
            );
            return "";
        }

        return `https://dosp-${envName[1]}-api.azurewebsites.net`;
    }

    private static async setToken(): Promise<void> {
        // Voorkom meerdere gelijktijdige token requests
        if (AuthenticationService.tokenPromise) {
            await AuthenticationService.tokenPromise;
            return;
        }

        AuthenticationService.tokenPromise = (async () => {
            // Probeer eerst token uit sessionStorage te halen
            const existingToken = sessionStorage.getItem("token");
            const existingExpiry = sessionStorage.getItem("tokenExpiry");

            if (existingToken && existingExpiry) {
                AuthenticationService.token = existingToken;
                AuthenticationService.tokenExpiry = parseInt(existingExpiry);

                // Check of token nog geldig is
                if (!AuthenticationService.isTokenExpiringSoon()) {
                    AuthenticationService.tokenPromise = null;
                    return;
                }
            }

            // Haal nieuwe token op
            try {
                await AuthenticationService.exchangeSSOToken();
            } catch (error) {
                console.error("Error in getting token", error);
                AuthenticationService.handleCriticalError(
                    error instanceof Error ? error : new Error(String(error)),
                    'SSO Token Authentication'
                );
            } finally {
                AuthenticationService.tokenPromise = null;
            }
        })();

        await AuthenticationService.tokenPromise;
    }

    private static async exchangeSSOToken(): Promise<void> {
        try {
            // Haal Teams SSO token op met timeout (geen ingebouwde timeout in SDK v2)
            const timeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('getAuthToken timed out after 10s — is de app geladen binnen Teams?')), 10000)
            );
            const ssoToken = await Promise.race([
                authentication.getAuthToken({ silent: true }) as Promise<string>,
                timeout
            ]);

            // Exchange SSO token voor API token
            const response = await fetch(`${AuthenticationService.baseUrl}/api/AuthenticateAD`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${ssoToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Authentication failed: ${response.status} ${response.statusText}. ${errorText}`);
            }

            const data = await response.json();
            if (!data || !data.token) {
                throw new Error("Token is not found in the response");
            }

            // Sla token op
            AuthenticationService.token = data.token;

            // Parse en sla expiry op
            const payload = AuthenticationService.parseJwt(data.token);
            if (payload && payload.exp) {
                AuthenticationService.tokenExpiry = payload.exp;
                sessionStorage.setItem("tokenExpiry", payload.exp.toString());
            }

            sessionStorage.setItem("token", AuthenticationService.token);
        } catch (error) {
            console.error("Error in exchanging SSO token:", error);
            throw error;
        }
    }

    private static parseJwt(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error parsing JWT:', error);
            return null;
        }
    }

    private static isTokenExpiringSoon(): boolean {
        if (!AuthenticationService.tokenExpiry) return true;

        const now = Date.now();
        const expiryTime = AuthenticationService.tokenExpiry * 1000;
        const fiveMinutes = 5 * 60 * 1000;

        return expiryTime - now < fiveMinutes;
    }

    private static startTokenRefreshInterval(): void {
        if (AuthenticationService.refreshInterval) {
            clearInterval(AuthenticationService.refreshInterval);
        }

        // Check elke 5 minuten of token moet worden gerefreshed
        AuthenticationService.refreshInterval = setInterval(async () => {
            if (AuthenticationService.isTokenExpiringSoon()) {
                await AuthenticationService.forceRefresh();
            }
        }, 5 * 60 * 1000);
    }

    private static handleCriticalError(error: Error, context: string): void {
        console.error(`Critical error in ${context}:`, error);

        const errorDetails = {
            message: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            context: context
        };

        sessionStorage.setItem('errorDetails', JSON.stringify(errorDetails));
        window.location.hash = '#/error';
    }
}
